import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import ConfirmAdvise from "@components/ui/ConfimDialogueAdvise";
import { NavigationPrompt } from "@utils/NavigationBlocker";
import { useTranslation } from "react-i18next";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
import { getAllFormFields, updateAllFormFields } from "api/preferences";
import { getAutoPreference } from "api/generalServices";
import MainHeader from "@components/ui/MainHeader";
import HeaderImage from "@assets/images/AdviseMeHeader.svg";
import AdviseMeSidePic from "@assets/images/AdviseMeSidePic.svg";
// exercise metadata APIs are not used here; they were present previously
import { useUI } from "context/LoadingContext";
import { useMeta } from "context/ExerciseMeta";


import {
  exerciseTimeOptions,
  daysOptions,
  timelineOptions,
  durationTypeOptions,
} from 'data/dropdowndData/adviseMeOptions';
import EditableInput from "@features/AdviseMe/EditableInput";
import SelectField from "@features/AdviseMe/SelectField";
import AdviseMeRightPanel from "@features/AdviseMe/AdviseMeRightPanel";
import ExercisePreferences from "@features/AdviseMe/ExercisePreferences";

const AdviseMe = () => {
  const [isAdviceLoading, setIsAdviceLoading] = useState(false);
  const { setIsActionLocked } = useUI();
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [selectedTags, setSelectedTags] = useState({});
  const [message, setMessage] = useState(null);
  const [advisedConfirm, setAdviseConfirm] = useState(null);
  const [autoSubmit, setAutoSubmit] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const { dynamicOptions } = useMeta();
  const countdownRef = useRef(null);
  const { t } = useTranslation();


  // Helper to read a field from selectedTags (stable reference)
  const getFieldValue = useCallback(
    (field) => {
      return selectedTags?.[field] ?? "";
    },
    [selectedTags]
  );

  // Helper to set a field on selectedTags (stable reference)
  const setFieldValue = useCallback((field, value) => {
    setSelectedTags((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Calculate daily calorie adjustment from selectedTags
  const calculatedDailyCalorieAdjustment = useMemo(() => {
    const target = Number(getFieldValue("targetDailyCalories"));
    const maintenance = Number(getFieldValue("maintenanceCalories"));
    if (
      !isNaN(target) &&
      !isNaN(maintenance) &&
      target !== 0 &&
      maintenance !== 0
    ) {
      return Math.round(target - maintenance);
    }
    return 0;
  }, [selectedTags?.targetDailyCalories, selectedTags?.maintenanceCalories]);

  // Sync calculated value to selectedTags
  useEffect(() => {
    if (
      calculatedDailyCalorieAdjustment !==
      getFieldValue("dailyCalorieAdjustment")
    ) {
      setFieldValue("dailyCalorieAdjustment", calculatedDailyCalorieAdjustment);
    }
  }, [calculatedDailyCalorieAdjustment]);

  useEffect(() => {
    return () => {
      setIsActionLocked(false);
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const data = await getAllFormFields();
      const tags = data?.selectedPreferences || {};
      setSelectedTags(tags);

      if (tags?.adviseMeDescription) {
        setMessage(tags.adviseMeDescription);
      }
    } catch (error) {
      console.error(
        "Error fetching diet plan:",
        error.response ? error.response.data : error.message
      );
    } finally {
      setFirstLoad(false);
    }
  };

  // Field validation configuration
  const fieldConfigs = {
    targetWeight: { label: t(`firstFormSteps.targetWeight.label`), type: 'string' },
    maintenanceCalories: { label: t(`firstFormSteps.maintenanceCalories.label`), type: 'string' },
    targetDailyCalories: { label: t(`firstFormSteps.targetDailyCalories.label`), type: 'string' },
    timeline: { label: t(`firstFormSteps.timeline.label`), type: 'string' },
    durationType: { label: t(`firstFormSteps.durationType.label`), type: 'string' },
    goalBodyFat: { label: t("Goal Body Fat %"), type: 'string' },
    goalWaistCircumference: { label: t("Goal Waist Circumference (cm)"), type: 'string' },
    goalSteps: { label: t("preferences.fields.goalSteps"), type: 'string' },
    targetActiveTime: { label: t("preferences.fields.targetActiveTime"), type: 'string' },
    dailyExerciseTimePreference: { label: t("Daily Exercise Time Preference"), type: 'string' },
    equipmentCategories: { label: t("Equipment Category"), type: 'string' },
    difficultiesLevel: { label: t("Difficulty Level"), type: 'string' },
    workoutsCategories: { label: t("Workout Categories"), type: 'array' },
    selectableDays: { label: t("Training Frequency"), type: 'array' },
  };

  // Generic validation function
  const validateFields = (data, fieldsToValidate = null) => {
    const newErrors = {};
    const fields = fieldsToValidate || Object.keys(fieldConfigs);

    fields.forEach(field => {
      const config = fieldConfigs[field];
      if (!config) return;

      const value = data[field];
      const isEmpty = config.type === 'array' 
        ? !value || value.length < 1 
        : !value || value === "";

      if (isEmpty) {
        newErrors[field] = t("validation.required", { field: config.label });
      }
    });

    return newErrors;
  };

  const validateSingleField = (field, value) => {
    const newErrors = { ...errors };
    const config = fieldConfigs[field];
    if (!config) return;

    const isEmpty = config.type === 'array' 
      ? !value || value.length < 1 
      : !value || value === "";

    if (isEmpty) {
      newErrors[field] = t("validation.required", { field: config.label });
    } else {
      delete newErrors[field];
    }

    setErrors(newErrors);
  };

  // Updated handleInputChange for text/number inputs
  const handleInputChange = (field, value) => {
    // Update the field value
    setFieldValue(field, value);

    // Validate the field immediately
    validateSingleField(field, value);
  };

  // Updated handleSelectChange for dropdowns
  const handleSelectChange = (field, value) => {
    setFieldValue(field, value);

    // Validate the field immediately
    validateSingleField(field, value);
  };

  // Updated handleMultiSelectChange
  const handleMultiSelectChange = (field, values) => {
    let processedValues = [...values];

    if (field === "selectableDays") {
      const lastSelected = values[values.length - 1];

      if (lastSelected === "All Days") {
        processedValues = ["All Days"];
      } else {
        processedValues = values.filter((v) => v !== "All Days");
      }
    }

    setFieldValue(field, processedValues);

    // Validate the field immediately
    validateSingleField(field, processedValues);
  };

  // Updated validateForm (just for final validation before submit)
  const validateForm = () => {
    // Clear all errors first
    setErrors({});

    const newErrors = validateFields(selectedTags);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleApplyGoal = () => {
    if (!validateForm()) {
      toast.error(t("toast.fillFields"));
      return;
    }

    // Ensure all fields are in selectedTags
    const updatedTags = {
      ...selectedTags,
      adviseMeDescription: message,
      dailyCalorieAdjustment: calculatedDailyCalorieAdjustment,
    };

    handleAutoSubmit(updatedTags);
  };

  const startCountdown = () => {
    setCountdown(60);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 0) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const validateCommingData = (dataToValidate) => {
    setErrors({});

    const newErrors = validateFields(dataToValidate);

    // 2. Update the error state with any new errors found.
    setErrors(newErrors);

    // 3. Return true if no errors were found, false otherwise.
    return Object.keys(newErrors).length === 0;
  };

  const handleAdvice = async () => {
    setIsAdviceLoading(true);
    setIsActionLocked(true);
    setFormSubmitting(true);
    startCountdown();

    try {
      const response = await getAutoPreference({
        Height: selectedTags?.height,
        Weight: selectedTags?.weight,
        "Your Age": selectedTags?.yourAge,
        "Weight Goal": selectedTags?.weightGoal,
        "Gain Muscle": selectedTags?.gainMuscle,
        "Weight Unit": selectedTags?.weightUnit,
        "Target Weight Unit": selectedTags?.targetWeightUnit,
        activity: selectedTags?.activity,
      });

      const data = response?.data;

      // Update all fields in selectedTags at once
      const updatedTags = {
        ...selectedTags,
        durationType: data?.durationType,
        targetWeight: data?.finalTargetWeight,
        targetWeightUnit: data?.targetWeightUnit,
        goalWaistCircumference: data?.goalWaistCircumference,
        goalBodyFat: data?.goalBodyFat,
        timeline: data?.timeline,
        dailyCalorieAdjustment: data?.dailyCalorieAdjustment,
        maintenanceCalories: data?.maintenanceCalories,
        targetDailyCalories: data?.targetDailyCalories,
        workoutsCategories: data?.workoutsCategories,
        equipmentCategories: data?.equipmentCategories,
        difficultiesLevel: data?.difficultiesLevel,
        dailyExerciseTimePreference: data?.dailyExerciseTimePreference,
        selectableDays: data?.selectableDays,
        // detailTargetBurnCalories: data?.detailTargetBurnCalories,
        goalSteps: data?.goalSteps,
        targetActiveTime: data?.targetActiveTime,
      };

      // 1. Update the state (asynchronously)
      setSelectedTags(updatedTags);

      // 2. 🌟 VALIDATE SYNCHRONOUSLY using the new object
      validateCommingData(updatedTags);

      setMessage(data?.message);
      toast.info(t("toast.adviceGenerated"));
    } catch (error) {
      console.log(error);
      toast.error(t("toast.Failed to get advice"));
    } finally {
      setIsAdviceLoading(false);
      setIsActionLocked(false);
      clearInterval(countdownRef.current);
      setCountdown(0);
    }
  };
  useEffect(() => {
    if (countdown === 2 && isAdviceLoading) {
      setCountdown((prev) => prev + 10);
    }
  }, [countdown, isAdviceLoading]);

  const handleWeightUnitChange = (newUnit) => {
    const currentWeight = getFieldValue("targetWeight");
    if (currentWeight) {
      const convertedWeight =
        newUnit === "Lbs" ? currentWeight * 2.20462 : currentWeight / 2.20462;
      setFieldValue("targetWeight", Math.round(convertedWeight));
    }
    setFieldValue("targetWeightUnit", newUnit);
  };

  const handleAutoSubmit = async (updatedTags) => {
    setAutoSubmit(true);
    try {
      await updateAllFormFields(updatedTags);
      toast.info(t("toast.informationSubmit"));
    } catch (error) {
      console.log(error);
      toast.error(t("toast.Failed to get advice"));
    } finally {
      setAutoSubmit(false);
      setFormSubmitting(false);
    }
  };

  const handleClose = (key) => {
    if (key === "cancel") {
      setFormSubmitting(false);
      setOpen(false);
    } else {
      handleAdvice();
      setOpen(false);
    }
  };



  // Helper functions for dynamic options
  const getDynamicDropdownOptions = useCallback(
    (key) => {
      const options = dynamicOptions[key] || [];
      if (key === "difficultiesLevel") {
        return options.map((opt) => ({
          value: opt,
          label: opt.charAt(0).toUpperCase() + opt.slice(1),
        }));
      } else {
        return options.map((opt) => ({
          value: opt.id,
          label: opt.name,
        }));
      }
    },
    [dynamicOptions]
  );

  const getDisplayMap = useCallback(
    (key) => {
      const options = dynamicOptions[key] || [];
      const displayMap = {};
      if (key === "difficultiesLevel") {
        options.forEach((opt) => {
          displayMap[opt] = opt.charAt(0).toUpperCase() + opt.slice(1);
        });
      } else {
        options.forEach((opt) => {
          displayMap[opt.id] = opt.name;
        });
      }
      return displayMap;
    },
    [dynamicOptions]
  );

  const getDisplayName = useCallback(
    (key, categoryId = null) => {
      if (!categoryId || !dynamicOptions?.[key]) return categoryId || "";
      if (key === "difficultiesLevel") {
        return categoryId.charAt(0).toUpperCase() + categoryId.slice(1);
      }
      const item = dynamicOptions[key].find((item) => item?.id === categoryId);
      return item ? item.name : categoryId;
    },
    [dynamicOptions]
  );

  return (
    <>
      <NavigationPrompt
        when={isSubmitting || isAdviceLoading || formSubmitting}
      />

      {firstLoad ? (
        <ActualDietPlanLoader adviseMe={true} />
      ) : (
        <>
          <MainHeader
            heading={t(`Get Personalized Health Advice Instantly`)}
            description={t("firstFormSteps.adviseMeDescription")}
            Image={HeaderImage}
            adviseMe={true}
          />

          <div className="bg-white p-3 rounded-[10px] mt-7">
            <h2 className="text-[20px] md:text-[24px] md:text-center font-[600] font-[Lato] text-black-600 px-4">
              {t("firstFormSteps.lastStepHeading")}
            </h2>
            <p className="text-[#535862] text-[16px] md:text-center px-4">
              {t("firstFormSteps.lastStepSubHeading")}
            </p>

            <div className="flex justify-center mt-2">
              <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 md:gap-3 w-full">
                {/* Left Column - Input Fields */}
                <div className="xl:col-span-3 p-4">
                  <h2 className="text-[16px] md:text-[20px] font-[600] font-[Lato] text-black-600 mb-4">
                    {t("firstFormSteps.automaticGoalHeading")}
                  </h2>

                  {/* Get Advice Button */}
                  <div className="mb-6">
                    <button
                      type="button"
                      onClick={() => setOpen(true)}
                      className={`px-4 py-[10px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-md w-full ${
                        isAdviceLoading && "opacity-70"
                      }`}
                      disabled={isAdviceLoading}
                    >
                      {isAdviceLoading
                        ? `(${countdown}s)`
                        : t("firstFormSteps.getAdvise")}
                    </button>
                  </div>

                  {/* Manual Goal Heading */}
                  <h2 className="text-[16px] md:text-[20px] font-[600] font-[Lato] my-4 text-center font-[600] text-black-600">
                    {t("firstFormSteps.manualGoal")}
                  </h2>

                  {/* Input Fields Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <EditableInput
                        field="targetWeight"
                        label={t(`firstFormSteps.targetWeight.label`)}
                        value={getFieldValue('targetWeight')}
                        targetWeightUnit={getFieldValue('targetWeightUnit')}
                        onChange={handleInputChange}
                        onWeightUnitChange={handleWeightUnitChange}
                        errors={errors}
                        isAdviceLoading={isAdviceLoading}
                        t={t}
                      />
                    </div>

                    <div>
                      <EditableInput
                        field="goalBodyFat"
                        label={t('Goal Body Fat')}
                        unit="%"
                        value={getFieldValue('goalBodyFat')}
                        onChange={handleInputChange}
                        errors={errors}
                        isAdviceLoading={isAdviceLoading}
                        t={t}
                      />
                    </div>

                    <div>
                      <EditableInput
                        field="goalWaistCircumference"
                        label={t('Goal Waist Circumference')}
                        unit="cm"
                        value={getFieldValue('goalWaistCircumference')}
                        onChange={handleInputChange}
                        errors={errors}
                        isAdviceLoading={isAdviceLoading}
                        t={t}
                      />
                    </div>

                    <div>
                      <EditableInput
                        field="maintenanceCalories"
                        label={t(`firstFormSteps.maintenanceCalories.label`)}
                        value={getFieldValue('maintenanceCalories')}
                        onChange={handleInputChange}
                        errors={errors}
                        isAdviceLoading={isAdviceLoading}
                        t={t}
                      />
                    </div>

                    <div>
                      <EditableInput
                        field="targetDailyCalories"
                        label={t(`firstFormSteps.targetDailyCalories.label`)}
                        value={getFieldValue('targetDailyCalories')}
                        onChange={handleInputChange}
                        errors={errors}
                        isAdviceLoading={isAdviceLoading}
                        t={t}
                      />
                    </div>

                    <div>
                      <EditableInput
                        field="dailyCalorieAdjustment"
                        label={t(`preferences.fields.dailyCalorieAdjustment`)}
                        unit="Kcal"
                        isReadOnly={true}
                        value={getFieldValue('dailyCalorieAdjustment')}
                        onChange={handleInputChange}
                        errors={errors}
                        isAdviceLoading={isAdviceLoading}
                        t={t}
                      />
                    </div>

                    <div>
                      <EditableInput
                        field="goalSteps"
                        label={t(`preferences.fields.goalSteps`)}
                        value={getFieldValue('goalSteps')}
                        onChange={handleInputChange}
                        errors={errors}
                        isAdviceLoading={isAdviceLoading}
                        t={t}
                      />
                    </div>

                    {/* <div>
                      <EditableInput
                        field="detailTargetBurnCalories"
                        label={t(`preferences.fields.detailTargetBurnCalories`)}
                        value={getFieldValue('detailTargetBurnCalories')}
                        onChange={handleInputChange}
                        errors={errors}
                        isAdviceLoading={isAdviceLoading}
                        t={t}
                      />
                    </div> */}

                    <div>
                      <EditableInput
                        field="targetActiveTime"
                        label={t(`preferences.fields.targetActiveTime`)}
                        value={getFieldValue('targetActiveTime')}
                        onChange={handleInputChange}
                        errors={errors}
                        isAdviceLoading={isAdviceLoading}
                        t={t}
                      />
                    </div>

                    <div>
                      <SelectField
                        field="timeline"
                        label={t(`firstFormSteps.timeline.label`)}
                        options={timelineOptions}
                        value={getFieldValue('timeline')}
                        onChange={handleSelectChange}
                        errors={errors}
                        isAdviceLoading={isAdviceLoading}
                        t={t}
                      />
                    </div>

                    <div>
                      <SelectField
                        field="durationType"
                        label={t(`firstFormSteps.durationType.label`)}
                        options={durationTypeOptions}
                        value={getFieldValue('durationType')}
                        onChange={handleSelectChange}
                        errors={errors}
                        isAdviceLoading={isAdviceLoading}
                        t={t}
                      />
                    </div>
                  </div>

                  {/* Exercise Preferences Section - extracted below */}
                </div>

                {/* Right Column - Professional Advice */}
                <AdviseMeRightPanel message={message}  AdviseMeSidePic={AdviseMeSidePic} t={t} />
              </div>
            </div>
            <div className="mt-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <ExercisePreferences
                getFieldValue={getFieldValue}
                getDynamicDropdownOptions={getDynamicDropdownOptions}
                handleMultiSelectChange={handleMultiSelectChange}
                handleSelectChange={handleSelectChange}
                getDisplayName={getDisplayName}
                getDisplayMap={getDisplayMap}
                errors={errors}
                isAdviceLoading={isAdviceLoading}
                daysOptions={daysOptions}
                exerciseTimeOptions={exerciseTimeOptions}
                t={t}
              />
            </div>

            <button
              type="button"
              onClick={handleApplyGoal}
              disabled={isAdviceLoading || autoSubmit}
              className={`mt-6 px-4 py-[10px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-md w-full min-h-[46px] ${
                (isAdviceLoading || autoSubmit) && "opacity-70"
              }`}
            >
              {autoSubmit ? (
                <ButtonLoader />
              ) : (
                t("firstFormSteps.navigationButtons.submit")
              )}
            </button>
          </div>
        </>
      )}

      {/* Confirmation Dialog */}
      <ConfirmAdvise
        open={open}
        handleClose={handleClose}
        isLoading={isAdviceLoading}
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        dynamicOptions={dynamicOptions}
        heading={t("firstFormSteps.Confirm Personalized Advice")}
        message={t(
          "firstFormSteps.Would you like to get personalized advice based on the fields below?"
        )}
        adviseMe={true}
      />
    </>
  );
};

export default AdviseMe;
